import { create } from 'zustand';
import type { Permission, RoleKey } from '@salon/shared';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: RoleKey;
  permissions: Permission[];
  staffId: string | null;
  branchIds: string[] | null;
}

export interface SessionSalon {
  id: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  locale: string;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  headingFont: string;
  bodyFont: string;
  branches: { id: string; name: string; city: string }[];
}

export interface Session {
  user: SessionUser;
  salon: SessionSalon | null;
}

interface AuthState {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  setSession: (s: Session | null) => void;
}

export const useAuth = create<AuthState>((set) => ({
  session: null,
  status: 'loading',
  setSession: (session) => set({ session, status: session ? 'authenticated' : 'anonymous' }),
}));

/** Permission check for hiding UI (the API enforces the same rules). */
export function useCan() {
  const perms = useAuth((s) => s.session?.user.permissions);
  return (p: Permission | Permission[]) => {
    const list = Array.isArray(p) ? p : [p];
    return !!perms && list.every((x) => perms.includes(x));
  };
}

export function useSalon() {
  const salon = useAuth((s) => s.session?.salon);
  if (!salon) throw new Error('useSalon used outside a salon session');
  return salon;
}
